// Kvitteringer — bruker ser egne kvitteringer, kan åpne HTML-versjon.
// Tilgjengelig fra profil-meny og skaperstudio-sidebar.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nb-NO", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function fmtNok(v) {
  const n = Number(v || 0);
  return `${n.toFixed(2).replace(".", ",")} kr`;
}

export default function Kvitteringer() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/receipts");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openHtml = (rid) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/me/receipts/${rid}/html`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="kvitteringer-page">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-editorial text-3xl text-[var(--cream)] mb-1">Kvitteringer</h1>
            <p className="text-sm text-[var(--cream-soft)]">
              Alle dine kjøp — hent kvittering til regnskap.
            </p>
          </div>
          <button
            onClick={load}
            className="px-3 py-2 border border-white/15 text-xs font-meta tracking-wider hover:border-[var(--magenta)] hover:text-[var(--magenta)] flex items-center gap-2"
            data-testid="kvitteringer-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> OPPDATER
          </button>
        </div>

        {loading ? (
          <p className="text-[var(--cream-faint)]">Laster…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-white/10">
            <FileText className="w-8 h-8 text-[var(--cream-faint)] mx-auto mb-3" />
            <p className="text-[var(--cream-soft)]">Ingen kvitteringer ennå.</p>
            <p className="text-xs text-[var(--cream-faint)] mt-2">
              Alle kjøp registreres her automatisk.
            </p>
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/5" data-testid="kvitteringer-list">
            {items.map((r) => (
              <div key={r.id} className="p-4 flex items-start justify-between gap-4 hover:bg-white/5 transition">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-meta text-sm text-[var(--cream)]">{r.product}</span>
                    <span className="text-[10px] px-1.5 py-0.5 border border-white/15 text-[var(--cream-faint)]">
                      {(r.provider || "").toUpperCase()}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--cream-faint)]">
                    {fmtDate(r.created_at)}
                  </div>
                  <div className="text-xs text-[var(--cream-soft)] mt-2">
                    Eks. MVA: {fmtNok(r.net_nok)} · MVA (25%): {fmtNok(r.vat_nok)} · <strong className="text-[var(--cream)]">{fmtNok(r.total_nok)}</strong>
                  </div>
                </div>
                <button
                  onClick={() => openHtml(r.id)}
                  className="shrink-0 flex items-center gap-1 px-3 py-2 text-xs font-meta tracking-wider border border-[var(--magenta)]/50 text-[var(--magenta)] hover:bg-[var(--magenta)]/10 transition"
                  data-testid={`kvitteringer-open-${r.id}`}
                >
                  <Download className="w-3 h-3" /> HENT
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 text-xs text-[var(--cream-faint)]">
          <p>Grøvdal Drift · Org.nr 928 828 247 · MVA-registrert</p>
          <p className="mt-1">Alle beløp inkluderer 25% mva. Trykk «HENT» for utskriftsvennlig versjon.</p>
          <p className="mt-2">
            <Link to="/profile" className="hover:text-[var(--magenta)]">← Tilbake til profil</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
